import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useLocalitiesStore } from '@/stores/localities'
import type { RouteLocationNormalized } from 'vue-router'

// Lazy load components
const LoginView = () => import('@/views/LoginView.vue')
const MeetingView = () => import('@/views/MeetingView.vue')
const MeetingDetailsView = () => import('@/views/MeetingDetailsView.vue')
const OAuthRedirectView = () => import('@/views/OAuthRedirectView.vue')
const UsersView = () => import('@/views/UsersView.vue')
const MeetingTypesView = () => import('@/views/MeetingTypesView.vue')
const UserProfileView = () => import('@/views/UserProfileView.vue')
const LoadingView = () => import('@/views/LoadingView.vue')
const LocalitiesView = () => import('@/views/LocalitiesView.vue')
const RosterDraftView = () => import('@/views/RosterDraftView.vue')
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'root',
      redirect: '/loading',
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { requiresAuth: false },
    },
    {
      path: '/zoom/oauth/redirect',
      name: 'zoom-oauth-redirect',
      component: OAuthRedirectView,
      meta: { requiresAuth: true },
    },
    {
      path: '/localities/:localityId/meetings',
      name: 'meetings',
      component: MeetingView,
      meta: { requiresAuth: true },
    },
    {
      path: '/localities/:localityId/meetings/:meetingId',
      name: 'meeting-details',
      component: MeetingDetailsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/localities/:localityId/users',
      name: 'users',
      component: UsersView,
      meta: { requiresAuth: true, requiresLocalityAdmin: true },
    },
    {
      path: '/localities/:localityId/meeting-types',
      name: 'meeting-types',
      component: MeetingTypesView,
      meta: { requiresAuth: true, requiresLocalityAdmin: true },
    },
    {
      path: '/localities/:localityId/roster-draft',
      name: 'roster-draft',
      component: RosterDraftView,
      meta: { requiresAuth: true, requiresLocalityAdmin: true },
    },
    {
      path: '/profile',
      name: 'profile',
      component: UserProfileView,
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/localities',
      name: 'localities',
      component: LocalitiesView,
      meta: { requiresAuth: true, requiresGlobalAdmin: true },
    },
    {
      path: '/loading',
      name: 'loading',
      component: LoadingView,
    },
    // Catch all route for 404
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      redirect: '/loading',
    },
  ],
})

// Navigation guard
router.beforeEach(async (to: RouteLocationNormalized) => {
  console.debug('Navigating to', to.path)

  if (to.path === '/loading') {
    // Don't need to initialize auth store for loading page
    console.debug('Loading page, skipping auth store initialization')
    return true
  }

  const authStore = useAuthStore()
  const localitiesStore = useLocalitiesStore()
  await authStore.init()

  // Keep selected locality in sync with the route.
  // Vue Router may reuse the same component instance when only params change,
  // so we treat the URL as the source of truth for the active locality.
  const routeLocalityId = to.params.localityId as string | undefined
  if (routeLocalityId && authStore.isAuthenticated && authStore.user) {
    if (authStore.user.isGlobalAdmin) {
      if (!localitiesStore.localities.length) {
        await localitiesStore.fetchLocalities()
      }

      const target = localitiesStore.localities.find((l) => l.id === routeLocalityId)
      if (target && localitiesStore.selectedLocalityId !== target.id) {
        localitiesStore.setSelectedLocality(target)
      } else if (localitiesStore.selectedLocalityId !== routeLocalityId) {
        localitiesStore.setSelectedLocalityId(routeLocalityId)
      }
    } else {
      // Non-global users cannot switch localities. If someone edits the URL, force it back.
      const userLocalityId = authStore.user.locality?.id
      if (userLocalityId && userLocalityId !== routeLocalityId) {
        if (to.name) {
          return { name: to.name as string, params: { ...to.params, localityId: userLocalityId }, query: to.query }
        }
        return { path: to.path.replace(routeLocalityId, userLocalityId), query: to.query }
      }

      if (userLocalityId && localitiesStore.selectedLocalityId !== userLocalityId) {
        localitiesStore.setSelectedLocalityId(userLocalityId)
      }
    }
  }

  // If requires auth and not authenticated
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // For the meetings route, redirect to login without the locality parameter
    if (to.name === 'meetings') {
      return {
        name: 'login',
        query: { redirect: '/loading' }, // Redirect to loading after login
      }
    }
    // For other routes, store the attempted URL for redirecting
    return {
      name: 'login',
      query: { redirect: to.fullPath },
    }
  }

  // If going to login page and already authenticated
  if (to.name === 'login' && authStore.isAuthenticated) {
    return { name: 'loading' } // Changed from 'meetings' to 'loading'
  }

  // If requires admin and not admin

  if (
    to.meta.requiresLocalityAdmin &&
    (!authStore.isAuthenticated || !authStore.user?.isLocalityAdmin)
  ) {
    return { name: 'meetings' }
  }

  if (
    to.meta.requiresGlobalAdmin &&
    (!authStore.isAuthenticated || !authStore.user?.isGlobalAdmin)
  ) {
    return { name: 'meetings' }
  }

  return true
})

export default router
